/** Object-storage key layout. Everything for an org lives under its prefix. */
export const storageKeys = {
  original: (orgId: string, assetId: string, ext: string) => `orgs/${orgId}/assets/${assetId}/original${ext ? '.' + ext : ''}`,
  proxy: (orgId: string, assetId: string, ext = 'mp4') => `orgs/${orgId}/assets/${assetId}/proxy.${ext}`,
  /** Light 540p proxy for phones and slow connections (same container as the main proxy). */
  proxySd: (orgId: string, assetId: string, ext = 'mp4') => `orgs/${orgId}/assets/${assetId}/proxy-sd.${ext}`,
  audioProxy: (orgId: string, assetId: string) => `orgs/${orgId}/assets/${assetId}/proxy.m4a`,
  poster: (orgId: string, assetId: string) => `orgs/${orgId}/assets/${assetId}/poster.jpg`,
  filmstrip: (orgId: string, assetId: string) => `orgs/${orgId}/assets/${assetId}/filmstrip.jpg`,
  waveform: (orgId: string, assetId: string) => `orgs/${orgId}/assets/${assetId}/waveform.bin`,
  export: (orgId: string, jobId: string, ext: string) => `orgs/${orgId}/exports/${jobId}.${ext}`,
  /** Frame `index` of a still render (see RenderSettings.still). */
  still: (orgId: string, jobId: string, index: number) => `orgs/${orgId}/exports/${jobId}-${index}.jpg`,
  orgPrefix: (orgId: string) => `orgs/${orgId}/`,
  assetPrefix: (orgId: string, assetId: string) => `orgs/${orgId}/assets/${assetId}/`,
  /** Original of a file replaced in place: in its own folder under the asset, so nothing cached of the old one is reused. */
  replacement: (orgId: string, assetId: string, version: string, ext: string) => `orgs/${orgId}/assets/${assetId}/${version}/original${ext ? '.' + ext : ''}`,
};

/**
 * Where the files made from an asset's original go: next to the original when
 * it is the asset's own (a replaced original has its own folder), else (linked
 * originals) the asset's folder.
 */
export function derivedPrefix(orgId: string, assetId: string, storageKey: string): string {
  const own = storageKeys.assetPrefix(orgId, assetId);
  return storageKey.startsWith(own) ? storageKey.slice(0, storageKey.lastIndexOf('/') + 1) : own;
}

/** The light (540p) proxy stored next to a video proxy, or '' for other proxies. */
export function lightProxyKey(proxyKey: string): string {
  return /\/proxy\.(mp4|webm)$/i.test(proxyKey) ? proxyKey.replace(/proxy\.(mp4|webm)$/i, 'proxy-sd.$1') : '';
}

/** Returns the org id a key belongs to, or null if malformed. */
export function orgOfKey(key: string): string | null {
  const m = /^orgs\/([^/]+)\//.exec(key);
  return m ? m[1] : null;
}

// ---------------------------------------------------------------------------
// Never overwrite a source: conversions and exports write new objects only
// ---------------------------------------------------------------------------

/** Keys compare case-insensitively here: some storage (local disk on macOS/Windows) is. */
const sameKey = (a: string, b: string) => !!a && !!b && a.toLowerCase() === b.toLowerCase();

/** The asset id of a key under `orgs/<org>/assets/<asset>/`, or null. */
export function assetOfKey(key: string): string | null {
  const m = /^orgs\/[^/]+\/assets\/([^/]+)\//i.exec(key);
  return m ? m[1] : null;
}

/** True for an asset's original or proxy by the key layout (`original.*`, `proxy.*`, `proxy-sd.*`, also in a replacement's folder). */
export function isAssetSourceKey(key: string): boolean {
  return /^orgs\/[^/]+\/assets\/[^/]+\/([^/]+\/)?(original(\.[^/]*)?|proxy(-sd)?\.[^/]+)$/i.test(key);
}

/** The first asset whose original or proxy is stored at `key`, or null. */
export function sourceUsingKey<T extends { storageKey: string; proxyKey: string }>(key: string, assets: Iterable<T>): T | null {
  for (const a of assets) if (sameKey(a.storageKey, key) || sameKey(a.proxyKey, key)) return a;
  return null;
}

/**
 * Why a conversion may not write its output at `target.storageKey`, or null when
 * it may: the output is the new asset's own original (under its own prefix),
 * never the source asset's original or proxy, nor anything in the source's folder.
 */
export function convertTargetProblem(target: {
  orgId: string;
  assetId: string;
  storageKey: string;
  source: { assetId: string; storageKey: string; proxyKey?: string };
}): string | null {
  const { storageKey: key, source } = target;
  if (!key || key.includes('..')) return 'The conversion has no valid output location';
  if (sameKey(target.assetId, source.assetId)) return 'A conversion must create a new file, not replace its source';
  if (sameKey(key, source.storageKey) || sameKey(key, source.proxyKey ?? '')) return 'The conversion would overwrite its source file';
  const own = storageKeys.assetPrefix(target.orgId, target.assetId);
  if (!key.startsWith(own) || !/^original(\.[^/]*)?$/i.test(key.slice(own.length))) return 'The conversion output must be the new file’s own original';
  if (sameKey(assetOfKey(key) ?? '', source.assetId)) return 'The conversion would overwrite its source file';
  return null;
}

/** Why an export or render may not be stored at `key`, or null: exports live under `exports/`, never in the asset library. */
export function exportKeyProblem(orgId: string, key: string): string | null {
  if (!key || key.includes('..')) return 'Invalid export location';
  if (assetOfKey(key) !== null || isAssetSourceKey(key)) return 'An export must never be written over a media file in the library';
  if (!key.startsWith(`${storageKeys.orgPrefix(orgId)}exports/`)) return 'Exports are stored under the organization’s exports';
  return null;
}

export function extOf(filename: string): string {
  const m = /\.([a-zA-Z0-9]{1,8})$/.exec(filename);
  return m ? m[1].toLowerCase() : '';
}

const MEDIA_MIME_BY_EXT: Record<string, string> = {
  mp4: 'video/mp4', mov: 'video/quicktime', mkv: 'video/x-matroska', webm: 'video/webm', avi: 'video/x-msvideo', mxf: 'video/mxf',
  mp3: 'audio/mpeg', wav: 'audio/wav', aac: 'audio/aac', m4a: 'audio/mp4', flac: 'audio/flac', ogg: 'audio/ogg',
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif', heic: 'image/heic', tif: 'image/tiff', tiff: 'image/tiff',
};

/** MIME type for a video, audio or image file extension, or null if unknown. */
export function mediaMimeForExt(ext: string): string | null {
  return MEDIA_MIME_BY_EXT[ext.toLowerCase()] ?? null;
}

/** Filmstrip layout produced by the media worker. */
export const FILMSTRIP = { tileWidth: 160, columns: 10, maxTiles: 100 } as const;

/** Waveform peaks resolution (peak pairs per second). */
export const WAVEFORM_PEAKS_PER_SECOND = 100;
